'use client';

import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Grid, HStack, VStack } from '@chakra-ui/react';
import { LoadingState } from '@/components/common/LoadingState';
import CommonContainer from "@/components/common/CommonContainer";
import { TraderDetailHeader } from '@/components/trader/TraderDetailHeader';
import { TagsSection } from '@/components/trader/TagsSection';
import { StorySection } from '@/components/trader/StorySection';
import { useCoinIcons } from '@/lib/hooks/useCoinIcons';
import { useTraderProfile } from '@/lib/hooks/useTraderProfile';
import { mockHomeContentList } from '@/lib/data/mockHomeData';
import { renderTagColor } from '@/lib/utils/tagColor.utils';
import { DownloadMobileAppCard } from '@/components/trader/DownloadMobileAppCard';
import { TraderAccountStats } from '@/components/trader/TraderAccountStats';
import { PortfolioChart } from '@/components/trader/PortfolioChart';
import { TradeStoryOverviewList } from '@/components/trader/TradeStoryOverviewList';
import { TraderProfile } from '@/components/trader/TraderProfile';
import { PortfolioContent } from '@/components/trader/PortfolioContent';

interface TraderData {
  user: {
    address: string;
    nickname?: string;
  };
  description?: string;
  labels?: string[];
  isFollowed?: boolean;
}

export default function TraderDetailPagePC () {
  const params = useParams();
  const address = params.address as string;

  const { data: traderProfile } = useTraderProfile(address);

  const { fetchIcons } = useCoinIcons();

  const [traderData, setTraderData] = useState<TraderData | null>(null);
  const sectionRefs = useRef<Record<string, number>>({});

  // Store section positions for scrollTo
  const setSectionRef = (sectionId: string, element: HTMLElement | null) => {
    if (element) {
      sectionRefs.current[sectionId] = element.offsetTop - 120;
    }
  };

  useEffect(() => {
    fetchIcons();
  }, []);

  useEffect(() => {
    // Mock trader data - replace with actual API call
    const foundTrader = mockHomeContentList[0];
    setTraderData({ ...foundTrader, user: { ...foundTrader?.user, address } });
  }, [address]);

  if (!traderData) {
    return (
      <Box minH="100vh" bg="black">
        <LoadingState />
      </Box>
    );
  }
  
  return (
    <CommonContainer borderTop="1px solid rgba(255, 255, 255, 0.10)" bg="#000" overflow="auto">
      <Grid pt={4} gap={10} templateColumns={{ base: '1fr', md: '7fr 3fr' }}>
        <VStack flex={1} gap={4} align="flex-start">
          <TraderDetailHeader
            traderName={traderProfile?.nickname ?? '- -'}
            traderAddress={address}
          />

          {/* Tags Section */}
          <TagsSection
            tags={renderTagColor(traderProfile?.labels ?? [])}
          />

          {/* Story Section */}
          <StorySection
            data={{
              description: traderProfile?.trader ?? '- -',
              address,
              nickname: traderProfile?.nickname ?? '- -',
            }}
          />
        </VStack>

        <DownloadMobileAppCard traderName={traderProfile?.nickname ?? '- -'} />
      </Grid>

      <Grid mt={6} gap={6} templateColumns={{ base: '1fr', md: '4fr 6fr' }}>
        {/* Account Stats with Expandable Content */}
        <TraderAccountStats address={address} />

        {/* Chart Section */}
        <PortfolioChart userAddress={address} />
      </Grid>

      {/* Trade Story Overview List */}
      <TradeStoryOverviewList address={address} />

      <TraderProfile traderAddress={address} />

      <Box mt={6}>
        <PortfolioContent userAddress={address as `0x${string}`} />
      </Box>
    </CommonContainer>
  )
}